import { BILLING_MODELS, type BillingModel, type EngagementInput } from '@clockwise/clients/sows';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { EngagementForm } from '@/components/custom/engagement-form';
import type { ProductDisplayLookup } from '@/components/custom/product-label';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { rateError } from '@/lib/rate-validation';

const MODEL_LABELS: Record<BillingModel, string> = {
  retainer: 'Retainer',
  tm: 'Time & materials',
  metrics: 'Metrics',
};

/**
 * Side sheet for editing ONE engagement. Used in the SOW page's amendment
 * flow:
 *
 *   - Caller passes `value` — the engagement being edited, or a blank one when
 *     adding.
 *   - The user edits a local draft inside the sheet; Save emits it via
 *     `onSave`, Cancel discards everything.
 *   - The caller stages the saved draft; the amendment commits when the user
 *     clicks "Save amendment" on the page itself.
 *
 * The billing model is switchable while ADDING and fixed once the engagement
 * exists: changing what an engagement IS after time and invoices have been
 * booked against it would rewrite history rather than describe a new
 * agreement. Add a second engagement instead.
 */
export function EngagementSheet({
  open,
  mode,
  value,
  customerCadenceLabel,
  productLookup,
  roleLookup,
  onOpenChange,
  onSave,
  onDelete,
  onModelChange,
}: {
  open: boolean;
  mode: 'add' | 'edit';
  /** The engagement being edited. Null when the sheet is closed. */
  value: EngagementInput | null;
  customerCadenceLabel: string;
  productLookup: ProductDisplayLookup;
  roleLookup: (id: string) => string;
  onOpenChange: (open: boolean) => void;
  onSave: (engagement: EngagementInput) => void;
  /** Only meaningful in edit mode. Omitted = no delete button. */
  onDelete?: () => void;
  /** Build a blank draft of another model, for the add-mode switcher. The
   *  page owns it because a fresh T&M seeds its rates from the rate card. */
  onModelChange?: (model: BillingModel) => EngagementInput;
}) {
  // Local draft — the sheet edits in isolation so Cancel can truly discard.
  const [draft, setDraft] = useState<EngagementInput | null>(value);

  // Reset whenever the sheet opens with a different engagement: add mode gets
  // a blank draft per open, edit mode a different engagement per click.
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const handleSave = () => {
    if (!draft) return;
    onSave(draft);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="flex-row items-start justify-between gap-3 border-b">
          <div>
            <SheetTitle>
              {mode === 'add' ? 'Add engagement' : `Edit ${draft?.name || 'engagement'}`}
            </SheetTitle>
            <SheetDescription>
              {mode === 'add'
                ? 'Changes apply on Save amendment for this SOW.'
                : `${draft ? MODEL_LABELS[draft.billingModel] : ''}. Changes apply on Save amendment for this SOW.`}
            </SheetDescription>
          </div>
          {mode === 'edit' && onDelete ? (
            <Button type="button" size="sm" variant="ghost" onClick={onDelete}>
              <Trash2 className="size-4" /> Delete
            </Button>
          ) : null}
        </SheetHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
          {mode === 'add' && draft && onModelChange ? (
            <div className="space-y-2">
              <Label htmlFor="engagement-model">Billing model</Label>
              <Select
                value={draft.billingModel}
                onValueChange={(v) =>
                  setDraft(carryShared(draft, onModelChange(v as BillingModel)))
                }
              >
                <SelectTrigger id="engagement-model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BILLING_MODELS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {MODEL_LABELS[m]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {draft ? (
            <EngagementForm
              value={draft}
              customerCadenceLabel={customerCadenceLabel}
              productLookup={productLookup}
              roleLookup={roleLookup}
              onChange={setDraft}
            />
          ) : null}
        </div>

        <SheetFooter className="flex-row justify-end border-t">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={!draft || !isEngagementValid(draft)}>
            Save
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Move the fields every billing model shares onto a draft of another model.
 *
 * Only the model-specific block genuinely needs resetting — the name, term,
 * invoicing window and inherited policy mean the same thing whichever way the
 * engagement is billed. Replacing the whole draft threw away everything the
 * operator had typed the moment they corrected the model, which is the one
 * point in the flow where changing their mind is expected.
 *
 * The NEW draft's model-specific fields are kept as the caller seeded them
 * (a fresh T&M arrives pre-populated from the rate card), so this only ever
 * copies the shared half.
 */
function carryShared(from: EngagementInput, to: EngagementInput): EngagementInput {
  return {
    ...to,
    id: from.id,
    name: from.name,
    description: from.description,
    invoiceStart: from.invoiceStart,
    invoiceEnd: from.invoiceEnd,
    billingCadence: from.billingCadence,
    consolidate: from.consolidate,
    manual: from.manual,
    activities: from.activities,
  };
}

/**
 * Light client-side gate so an obviously incomplete draft can't be saved
 * (Save would just bounce off the server otherwise). Not exhaustive — the
 * server is the source of truth.
 */
export function isEngagementValid(engagement: EngagementInput): boolean {
  if (!engagement.name.trim()) return false;
  switch (engagement.billingModel) {
    case 'retainer':
      // Activities are optional — a retainer bills its fee whether or not it
      // names the work it covers. Overage rates are optional too, and are
      // only checked when some are present.
      return (
        Number.isFinite(engagement.fee) &&
        engagement.fee >= 0 &&
        (engagement.overageRates ?? []).every((r) => rateError(r.rate) === null)
      );
    case 'tm':
      return (
        engagement.rates.length > 0 &&
        engagement.rates.every((r) => Boolean(r.roleId) && rateError(r.rate) === null)
      );
    case 'metrics':
      return engagement.metricName.trim().length > 0 && engagement.tiers.length > 0;
  }
}
